import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import { Star } from 'lucide-react';

const formatMonthLabel = (m) => {
  if (!m) return '—';
  const [y, mo] = m.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[parseInt(mo, 10) - 1]} '${y.slice(2)}`;
};

const LowPerformersList = ({ label, rows = [], nameKey, onSelect }) => {
  const lowest = rows.filter(r => r.count > 0).slice(0, 3);
  return (
    <div className="flex-1 min-w-[180px]">
      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">{label}</p>
      {lowest.length > 0 ? (
        <div className="space-y-1.5">
          {lowest.map(r => (
            <button
              key={r[nameKey]}
              onClick={() => onSelect && onSelect(r[nameKey])}
              title={r[nameKey]}
              className="w-full flex items-center justify-between text-sm hover:bg-muted/60 rounded px-1 -mx-1 transition-colors group cursor-pointer"
              data-testid={`feedback-low-${nameKey}-${r[nameKey]}`}
            >
              <span className="truncate max-w-[120px] underline decoration-dotted group-hover:decoration-solid">{r[nameKey]}</span>
              <span className={`flex items-center gap-1 font-medium ${r.avg_rating < 3.5 ? 'text-red-600' : 'text-amber-600'}`}>
                <Star className="w-3 h-3 fill-current" />
                {r.avg_rating}/5
              </span>
            </button>
          ))}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">No feedback yet</p>
      )}
    </div>
  );
};

export const FeedbackTrendCard = ({ monthlyTrend = [], byTrainer = [], byPartner = [], byCourse = [], onDrill, testId = 'feedback-trend-card' }) => {
  const chartData = monthlyTrend.map(m => ({ ...m, monthLabel: formatMonthLabel(m.month) }));
  const hasData = chartData.some(d => d.avg_rating != null);

  return (
    <Card data-testid={testId}>
      <CardHeader>
        <CardTitle className="font-heading text-base">Feedback Trend Over Time</CardTitle>
        <CardDescription className="text-xs">MTM average (0–5 scale) by month, with lowest performers surfaced below</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {hasData ? (
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="monthLabel" tick={{ fontSize: 11 }} tickLine={false} />
              <YAxis domain={[0, 5]} tick={{ fontSize: 11 }} tickLine={false} />
              <RechartsTooltip formatter={(v) => (v != null ? `${v}/5` : 'No data')} />
              <Line type="monotone" dataKey="avg_rating" stroke="#8B5CF6" strokeWidth={2} dot={{ r: 3 }} connectNulls />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-center text-muted-foreground py-8 text-sm">No feedback data yet</p>
        )}
        <div className="flex flex-wrap gap-4 pt-3 border-t">
          <LowPerformersList label="Lowest — Trainer" rows={byTrainer} nameKey="trainer_name" onSelect={(v) => onDrill && onDrill('trainer', v)} />
          <LowPerformersList label="Lowest — Partner" rows={byPartner} nameKey="partner_name" onSelect={(v) => onDrill && onDrill('partner', v)} />
          <LowPerformersList label="Lowest — Course" rows={byCourse} nameKey="course_title" onSelect={(v) => onDrill && onDrill('course', v)} />
        </div>
      </CardContent>
    </Card>
  );
};

export default FeedbackTrendCard;
